// Missions, wearing the ofself shell.
//
// Region mapping:
//   the platform chrome ......... the kit's DOCK (the floating avatar) and the
//                                 top-right array (FOCUS) — shell 0.257.0
//   the page menu ............... the LEFT panel — SplitView leading, menu rows
//                                 (was the layer-2 app-nav rail)
//   the pages (Now/Missions/…) .. legacy .content markup  → block--fill
//   the Agent chat .............. legacy .chat-panel      → shell <SlideOutPanel>
//                                 (the RIGHT panel; body.chat-closed is only the
//                                 state channel app.js speaks — no styling rides it)
//   the command dock ............ shell-owned (⌘J, and the list MENU's Search)
//                                 — missions supplies the list (commands.js)
//
// The panels that host legacy DOM are memoized so a panel toggle never
// re-renders (and thus never wipes) what app.js painted into them.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Shell,
  ShellProvider,
  Landing,
  LandingMenu,
  LandingNav,
  ThemeToggle,
  useResizablePanel,
  usePanelOpen,
  SplitView,
  IdentityWall,
  setFocusMode as setFocusModeKit,
  isFocusMode,
  TileColumn,
  Button,
  ComposeIcon,
  useLoginLine,
  loginLineHint,
  CellViewPicker,
  PanelHeader,
  PanelMenu,
  EcosystemTable,
  Nucleus,
  NucleusIcon,
  SlideOutPanel,
} from '@ofself/shell';
import legacyAppUrl from './legacy/app.js?url';
import { parseBlocks } from '@ofself/shell';
import { MISSIONS_BLOCKS } from './blocks.mjs';
import { toShellGroup } from './lib/shellSpaces.mjs';
import { CONTENT_HTML, CHAT_HTML } from './legacy/markup.js';
import { buildMissionsCommands } from './commands.js';
import { EpBanner } from './components/EpBanner.jsx';
import MissionsOverview from './components/MissionsOverview';
import Lookback from './pages/Lookback';
import Manage from './pages/Manage';
import Now from './pages/Now';
import Schedule from './pages/Schedule';
import Mission from './pages/Mission';
import Builder from './pages/Builder';
import Sheet from './pages/Sheet';

// ── Nav items — a glyph above each word (GridNav's icon slot) ─────────────────
// One drawing voice: 24-grid, 1.6 stroke, round caps. THE EMBER MOTIF (found
// by accident in Manage's knobs on the dark skin, kept on purpose): the
// line-work rides currentColor at half opacity — scaffolding that dims and
// lifts with the cell — while the dots burn at FULL SHELL INK always, hovered
// or not. The embers are the one thing in the menu that never dims.
const I = (dim, embers) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <g opacity="0.45">{dim}</g>
    <g fill="var(--shell-ink, #111)" stroke="none">{embers}</g>
  </svg>
);

// THE ORDER IS THE ARGUMENT: the lattice first (what we are trying to do),
// and only then the queue of what's in front of you today. Plan before
// present — Now used to lead, which put the to-do list above the reason for it.
const NAV = [
  /* Missions — rings are scaffolding; the ember is the bullseye. */
  { id: 'missions', label: 'Missions', fn: 'navToMissions',
    icon: I(
      <><circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="4.5" /></>,
      <circle cx="12" cy="12" r="1.5" />,
    ) },
  /* Now — the clock is scaffolding; the ember is THIS instant: the centre
     pivot and the tip of the hour hand. */
  { id: 'now',      label: 'Now',      fn: 'navToNow',
    icon: I(
      <><circle cx="12" cy="12" r="8.5" /><polyline points="12 7.5 12 12 15.2 13.8" /></>,
      <><circle cx="12" cy="12" r="1.3" /><circle cx="15.2" cy="13.8" r="1.1" /></>,
    ) },
  /* Schedule — the calendar is scaffolding; the embers are the marked days:
     today, and the two ahead of it. */
  { id: 'schedule', label: 'Calendar', fn: 'navToSchedule',
    icon: I(
      <><rect x="3.5" y="5" width="17" height="15.5" rx="2.5" /><line x1="3.5" y1="9.5" x2="20.5" y2="9.5" /><line x1="8" y1="3" x2="8" y2="6.5" /><line x1="16" y1="3" x2="16" y2="6.5" /></>,
      <><circle cx="8" cy="13.4" r="1.2" /><circle cx="12" cy="13.4" r="1" /><circle cx="16" cy="17" r="1" /></>,
    ) },
  /* Lookback — the rewind sweep is scaffolding; the embers are then and now:
     where the arrow points back to, and the hands' pivot. */
  { id: 'lookback', label: 'Lookback', fn: 'navToLookback',
    icon: I(
      <><path d="M4.5 5v4.2h4.2" /><path d="M4.9 9.2a8.5 8.5 0 1 1-1.2 5" /><polyline points="12 8.5 12 12.5 14.8 14" /></>,
      <><circle cx="4.5" cy="9.2" r="1.1" /><circle cx="12" cy="12.5" r="1.2" /></>,
    ) },
  /* Manage retired from the page menu (owner call 2026-09-20): tags are the
     workspace's furniture, not a page you visit — they live in Settings ›
     Manage now (the Shell's `settings` panes, below). */
];

// ── Agent open/close/toggle — module-scope: pure DOM + the persisted key ──────
// body.chat-closed is the ONE channel (app.js speaks it too); the class
// observer in AuthedApp mirrors it into React state for the rail glyph and
// the panel's `open` prop.
function openAgent() {
  document.body.classList.remove('chat-closed');
  try { localStorage.removeItem('missions_new:chatClosed'); } catch {}
  setTimeout(() => document.getElementById('chat-input')?.focus(), 80);
}
function closeAgent() {
  document.body.classList.add('chat-closed');
  try { localStorage.setItem('missions_new:chatClosed', '1'); } catch {}
}
function toggleAgent() {
  if (document.body.classList.contains('chat-closed')) openAgent();
  else closeAgent();
}
// The dock's fallthrough seam → the agent thread (the fleet grammar: a line
// the dock can't parse and the list can't match is natural language — one
// agent, two mouths). Feeds the legacy composer through its own send path.
function askAgent(line) {
  openAgent();
  const input = document.getElementById('chat-input');
  if (!input) return;
  input.value = line;
  document.getElementById('chat-send')?.click();
}

// ── The left panel: the page menu ─────────────────────────────────────────────
// The old layer-2 app-nav rail, rehomed as a real panel: the kit's
// nav <TileColumn> (below, in AuthedApp) — the seam-line column of
// equal-height tiles (no icons, the tile IS the word; hover lifts to ink,
// active holds it). Cells call the same window.navTo* functions app.js
// exposes; the active cell tracks the nav-interception effect in AuthedApp.
// No Agent cell — the Agent is the RIGHT panel now, toggled from the rail
// like every other panel.
function goToPage(id) {
  if (id === 'home') { window.navToHome?.(); return; }
  const n = NAV.find((x) => x.id === id);
  if (n) window[n.fn]?.();
}

// The command dock has no programmatic door — its toggle is the shell-bound ⌘J
// (Shell.jsx's hotkey pipeline), so the list MENU's Search speaks the same key
// the keyboard would.
const toggleDock = () => {
  document.dispatchEvent(new KeyboardEvent('keydown', {
    key: 'j', metaKey: true, bubbles: true, cancelable: true,
  }));
};


// ── Agent panel (squeeze, panel-3) ───────────────────────────────────────────
// THE LEGACY CHAT DOM STAYS MOUNTED, AND HIDDEN (owner call 2026-08-17). The
// visible chat is the kit's conversational tier now, but thirteen call sites
// in legacy/app.js still reach for #chat-input / #chat-body / #chat-send —
// including the widget-choice path inside the proposal renderer. Removing the
// markup would turn those into silent null-derefs, so it stays in the tree as
// the legacy code's own scaffolding, painting nothing. It leaves for good when
// those call sites do.
const AgentBody = React.memo(function AgentBody() {
  return (
    <div className="block__body missions-agent-body" hidden aria-hidden="true"
         style={{ display: 'none' }}
         dangerouslySetInnerHTML={{ __html: CHAT_HTML }} />
  );
});

// The shell's Agent/Help & support sidebar — the SAME surface ink and synapse
// mount: slide animation, resize seam, the icon header with the built-in
// Quick Help toggle (hover-to-learn card) and Help & support mailto. Missions
// supplies only the legacy chat body and the new-chat action (its legacy id —
// app.js wires the conversation reset onto it). Children-mode is the
// THE CONVERSATIONAL TIER AT LAST (owner call 2026-08-17). This panel wore
// the kit's chrome around the LEGACY chat DOM — its own bubbles, avatars and
// timestamps inside the shell's frame, which is the drift the owner kept
// seeing. It stands on the kit's own tier now, and two seams (both added to
// the kit tonight, precisely because this app needed them) made it possible
// without losing anything:
//
//   · agent.stream — missions' llmChat streams, and the tier could only await
//     a single promise. Each chunk carries the whole text so far, which is
//     exactly what llmChat's onChunk already hands us.
//   · agent.renderText — the answers are NOT prose: fenced json/widget blocks
//     become pressable proposal cards that commit nodes to the graph
//     (renderAssistantContent). Rendered as text they would be raw code
//     fences and the propose-and-accept loop would die. The app paints its
//     own bubble body; the kit keeps the bubble, the hand and the register.
//
// Both legacy functions are top-level declarations in a classic script, so
// they stand on window at runtime — read through window at call time (never
// captured), so a reload of the legacy bundle cannot leave a stale reference.
/* THE RECORD IS SCOPED LIKE THE WRITE. These two reads are raw fetches — they do
   not pass through the legacy engine's fetchJson/maybeScopeUrl, which is what
   appends the active group to everything else — so without this they arrive with
   no group and the server, which reads req.groupId off the query or body and
   nowhere else, asks Personas for the PERSONAL agent's history. The thread was
   written by /llm/chat, which sets body.group_id for exactly the same reason and
   with the same comment. Write and read now name the same identity. */
function scopeUrl(url, groupId) {
  if (!groupId) return url;
  return `${url}${url.includes('?') ? '&' : '?'}group_id=${encodeURIComponent(groupId)}`;
}

const AgentPanel = React.memo(function AgentPanel({ open, onClose, onOpen, identityName, perms, agents, groupId }) {
  // THE WIDGET BRIDGE. A proposal's choice widget fills the (now hidden)
  // #chat-input and calls sendChat — legacy code we are not rewriting tonight.
  // Overriding that one function routes the line into the kit thread through
  // the tier's own nonce'd injection, so a choice still lands in the
  // conversation the user is actually looking at. Without this the click would
  // send into the hidden DOM and read as a dead button.
  const [ask, setAsk] = React.useState(null);
  React.useEffect(() => {
    const prev = window.sendChat;
    window.sendChat = () => {
      const el = document.getElementById('chat-input');
      const text = (el?.value || '').trim();
      if (!text) return;
      el.value = '';
      setAsk((a) => ({ n: (a?.n || 0) + 1, text }));
    };
    return () => { window.sendChat = prev; };
  }, []);
  /* THE SCOPE IS READ AT CALL TIME, NOT CAPTURE TIME.
     This object is memoized on [ask], so anything its closures read is frozen at
     the render that built it. groupId was being frozen at null and stayed null:
     the memo only rebuilds when `ask` changes, which happens when you SEND a
     message — so listing worked only after chatting, never on open.
     The legacy engine made this exact call and wrote down why: "maybeScopeUrl
     reads _activeGroupId at CALL time, so a request issued before boot has
     resolved the scope is scoped to whatever localStorage happened to hold."
     A ref keeps the object's identity stable, so the thread is not remounted
     every time the space resolves. */
  const groupIdRef = React.useRef(groupId);
  React.useEffect(() => { groupIdRef.current = groupId; }, [groupId]);
  const agent = React.useMemo(() => ({
    ask,
    placeholder: 'Ask anything',
    greetings: ["Ask me to break a mission down, name a habit, or surface what's slipping."],
    /* the wall's Personas seam: emit({text}) REPLACES with the answer so
       far — the legacy transport already accumulates, so its `full` is
       exactly that contract. */
    send: async (text, { emit, mode, incognito }) => {
      const send = window.llmChat;
      if (typeof send !== 'function') return 'The chat transport has not loaded yet.';
      const ctx = typeof window.chatContext === 'function' ? window.chatContext() : null;
      let final = '';
      /* TWO ACCUMULATORS, ONE CLEAR. `final` mirrors the transport's own running
         text so the return value can carry it — and the transport EMPTIES its copy
         when the kit reports a flush (legacy/app.js, on endMessage), while this one
         used to sail on holding the text the kit had just drawn. `onDone` then
         arrived with '' and the `if (full)` guard below correctly refused to
         overwrite, so `send` returned the flushed message and the kit — which adds
         a landing turn for whatever comes back — drew it a SECOND time. The reply
         appeared twice, byline and all.
         The kit's `true` is the signal and it only reaches the host through this
         wrapper; passing `emit` straight through threw it away. Its return value
         must survive untouched — the transport tests it with `=== true`. */
      const onEvent = (ev) => {
        const flushed = emit?.(ev);
        if (ev?.endMessage && flushed === true) final = '';
        return flushed;
      };
      // onDone is (full, conversationId, error). The thread id is the transport's
      // own business now — but the ERROR is ours, and dropping it is why a failed
      // send showed as an empty reply: on failure `full` is '', so `if (full)`
      // skipped it and this returned an empty string with nothing anywhere saying
      // the agent had refused, timed out, or needed re-authorization.
      await send(text, ctx, (_chunk, full) => { final = full; emit?.({ text: full }); },
        (full, _conversationId, err) => {
          if (full) final = full;
          if (err) final = final ? `${final}\n\n${err}` : String(err);
        }, mode, onEvent, incognito);
      // THE KIT DRAWS THEM NOW. Blocks come out of the prose and go back as
      // acts and choices; renderText below then paints plain text, because the
      // fences it used to turn into cards are no longer in it.
      return readAgentBlocks(final, `t${Date.now()}`);
    },

    /* APPLYING ONE. The kit owns the card and the two buttons; commitProposal
       still owns what a mission, goal, action or habit actually IS — including
       resolving turn-local refs by committing parents first, which is this
       app's model of its own hierarchy and not something the kit should learn.
       Dismiss needs no work: the card settles and nothing is written. */
    /* NO renderBlock: missions declares no custom blocks. Every one of its
       blocks maps to a kit card, which is the state to be in — the escape hatch
       is for what is genuinely one app's own, and a mission is not that. */
    onResolveAct: async (act, decision) => {
      if (decision !== 'apply') return;
      if (typeof window.commitProposal !== 'function') {
        throw new Error('The proposal engine has not loaded yet.');
      }
      await window.commitProposal(act.verb, act.data, _batches.get(act.batch));
    },
    // THE RECORD (owner call 2026-08-17): the panel's clock seat stands only
    // when the app can name its past threads, which is why it was missing —
    // this app streamed into a conversation and never listed what came
    // before. Both routes are Personas' own memory, signed server-side.
    sessions: async () => {
      try {
        const j = await fetch(scopeUrl('/api/agent/conversations', groupIdRef.current)).then((r) => r.json());
        return (j.conversations || []).map((c) => ({
          id: c.id,
          title: c.title || 'untitled thread',
          at: c.updated_at || c.created_at,
        }));
      } catch (_) { return []; }
    },
    // A NEW THREAD FORGETS THE OLD ONE: the wall's NEW (and a change of
    // incognito posture) must drop the transport's conversation id, or the next
    // word continues the thread that was just left
    onNewChat: () => window.setChatConversationId?.(null),
    onIncognito: () => window.setChatConversationId?.(null),
    onResume: async (row) => {
      try {
        const j = await fetch(scopeUrl(`/api/agent/conversations/${encodeURIComponent(row.id)}`, groupIdRef.current)).then((r) => r.json());
        // Through the setter, never by assigning that global directly: it is
        // declared `let`, which is NOT a property of window, so the assignment
        // landed on a different slot and the resumed thread was never continued.
        if (j.conversation_id) window.setChatConversationId?.(j.conversation_id);
        // THE SAME READING FOR A RESUMED TURN as for a live one. Handing the
        // raw text back left the blocks in the prose, where renderText's legacy
        // painter still finds them — so a thread you continued drew the OLD
        // cards while the turn you just sent drew the kit's. One conversation,
        // two card designs, decided by which half you were looking at.
        //
        // Keyed by message id so the batch survives a re-resume: refs resolve
        // against the same map they were parsed into, not a fresh one per read.
        return (j.messages || []).map((m, i) => {
          const text = String(m.content || m.text || '');
          if (m.role === 'user') return { role: 'user', text };
          const read = readAgentBlocks(text, `h${row.id}:${m.id || i}`);
          return { role: 'agent', ...read };
        });
      } catch (_) { return []; }
    },
    // the proposal cards, painted by the app that owns their verbs
    renderText: (text, el) => {
      if (typeof window.renderAssistantContent === 'function') window.renderAssistantContent(el, text);
      else el.textContent = text;
    },
  }), [ask]);
  /* THE WALL'S ONE OBJECT (ink's law): fetched once, every tab a reading.
     Fetched by AuthedApp and handed down. */
  const schemas = React.useMemo(() => schemasFrom(perms), [perms]);
  /* THE FOUR READINGS IN ONE DOOR (owner call 2026-08-25): the right edge is
     the kit's docked IdentityWall — Personas (the old agent panel's thread,
     same transport), Nucleus, Actions, Exposure. The tesseract seat retired
     with it; one logo opens the consolidated wall. */
  return (
    <>
      <IdentityWall
        docked
        /* The readings are the dock's rows, so the in-plate quad strip stands
           down — one choice, one seat. */
        strip={false}
        open={open}
        onClose={onClose}
        /* THE WALL REGISTERS ITS OWN DOOR (shell 0.253.0): the dock's reading
           rows and Personas seat open THIS wall on a reading */
        onOpen={onOpen}
        storageKey="missions-agent-width"
        appName="Missions"
        /* Names the person on their own turns and completes the Exposure
           sentence ("Missions is acting for <you> inside …"), which had been
           falling back to the generic word. */
        identityName={identityName || undefined}
        permissions={perms}
        agents={agents}
        personas={agent}
        library={{ load: loadLibrary, schemas }}
        ledger={{ load: loadLedger, onUndo: revertEntry }}
      />
    </>
  );
});

// ── The missions columns — the kit's sidebar grammar, fed by the engine ──────
// The census sidebar and the view column are SplitView leadings (fold, glide,
// drag seam, curtain and spine all the shell's own). The legacy engine
// announces data (missions:census / missions:page / missions:view) and takes
// presses back through its own globals (openMission, setMissionsSideSort,
// setMissionView) — one source of truth for order and routing.
import { loadAgents, loadLibrary, loadLedger, revertEntry, loadPermissions, schemasFrom }
  from './lib/identityWall';

const SIDE_SORTS = [
  { key: 'tier', label: 'status · intention' },
  { key: 'name', label: 'name (A to Z)' },
  { key: 'open', label: 'open actions (most first)' },
];
const MISSION_VIEWS = [
  { key: 'plan', label: 'goals' },
  { key: 'outline', label: 'actions' },
  /* 'board' retired from this column (2026-09-10): it is a VIEW inside
     Actions now, on the table's own views strip. */
  { key: 'signals', label: 'signals' },
];

// THE MISSION LIST'S VERBS — the kit's spread head over one items array
// (owner call 2026-09-24: the same head every list column wears, so it
// stacks one per row when the column is too tight for the fold grip). The
// folded spine carries the same items as <PanelMenu>, so the two can't drift.
const NEW_GLYPH = <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true"><path d="M12 5v14" /><path d="M5 12h14" /></svg>;
const SORT_GLYPH = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m3 16 4 4 4-4" /><path d="M7 20V4" /><path d="M11 4h10" /><path d="M11 8h7" /><path d="M11 12h4" /></svg>;
function missionListItems(census) {
  return [
    { id: 'new', label: 'New mission', glyph: NEW_GLYPH,
      onSelect: () => document.getElementById('new-mission-fab')?.click(),
      qhBody: 'Starts a new mission.' },
    { id: 'sort', label: 'Sort', glyph: SORT_GLYPH,
      items: SIDE_SORTS.map((s) => ({
        label: (
          <span className="missions-sort-row">
            <span className="missions-sort-label">{s.label}</span>
            {census.sort === s.key && <span className="missions-sort-check">✓</span>}
          </span>
        ),
        onSelect: () => window.setMissionsSideSort?.(s.key),
      })),
      qhBody: 'Orders the missions list.' },
  ];
}

// ── React pages inside legacy containers (the port's strangler seam) ─────────
// Each ported page renders through a PORTAL into its legacy .page container,
// so the legacy palette (scoped to .missions-content-body) and every page
// rule in style.css apply unchanged, and setPage's active-class routing keeps
// working with zero changes. The portal target exists from ContentPanel's
// first paint; `ready` flips after mount so the lookup never races it.
function LegacyPagePortal({ target, children }) {
  const [el, setEl] = useState(null);
  useEffect(() => { setEl(document.getElementById(target)); }, [target]);
  return el ? createPortal(children, el) : null;
}

// ── Content panel ─────────────────────────────────────────────────────────────
// Memoized with no props → mount once, app.js's imperative DOM is never wiped.
const ContentPanel = React.memo(function ContentPanel() {
  return (
    <main className="block block--fill missions-content"
          data-qh-title="The page"
          data-qh-body="The page you're on — pick another from the page menu on the left.">
      {/* the fade rides this inner body (display flips with
          body.missions-home re-run the animation), never the .block —
          a fading panel un-paints its own surface and the field leaks. */}
      <div className="block__body missions-content-body ofself-page-fade"
           dangerouslySetInnerHTML={{ __html: CONTENT_HTML }} />
    </main>
  );
});

// The detail sheet is the kit's right <SlideOutPanel> now (src/pages/Sheet.jsx),
// seated in the row beside the agent wall — no longer a modal portaled to
// <body> over the stage.

// Mirrors slugify() and ROUTE_PAGES in legacy/app.js — the URL grammar is
// /<scope>/<page>[/<extra>], and both halves of the app have to read the scope
// segment the same way or they disagree about which space you are in.
// Change together with legacy/app.js.
// The app's Settings panes (Shell `settings`): Manage — the workspace's
// furniture, tags today. Wears the desk's padded-pane dress so its controls
// start where the skin cards' labels do.
const MISSIONS_SETTINGS_PANES = [
  /* THE CELL VIEW, THE APP'S OWN (owner call 2026-09-22): tiles filling the
     column, or a compact list — the kit's <CellViewPicker>, which left the
     platform's Preferences (2026-09-21) for each app to seat where its own
     columns want the choice. Missions' page menu, census and view column
     all read it (html[data-cell-view]). */
  { id: 'cells', label: 'Cells', content: <div className="ofself-settings__pane-pad"><CellViewPicker title="" /></div> },
  { id: 'manage', label: 'Manage', content: <div className="ofself-settings__pane-pad"><Manage active /></div> },
];

const ROUTE_PAGE_SEGMENTS = ['home', 'missions', 'now', 'calendar', 'schedule', 'lookback', 'manage'];
function slugifyScope(s) {
  return String(s || '').toLowerCase().normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'space';
}

// ── Authed shell ──────────────────────────────────────────────────────────────

// ── The agent's blocks, read by the kit from ONE declaration ─────────────────
//
// The grammar is still missions' — json:create, widget:choice and the rest. What
// left is the second copy of it: this file held a hand-written parser while the
// server held a hand-written paragraph describing the same blocks, and the
// paragraph is the copy that rots quietly, because a model given a stale grammar
// produces plausible output that renders as nothing.
//
// MISSIONS_BLOCKS is now read by both — parseBlocks here, blockPrompt on the
// server — so a block cannot be described one way and handled another.
//
// THE BATCH STAYS APP-SIDE. Children created in one turn point at a parent by
// turn-local "ref", and commitProposal resolves those by committing parents
// first. That is missions' model of its own hierarchy; the kit has no business
// learning it, so acts carry a shared batch key and the map lives here.
const _batches = new Map();

function readAgentBlocks(full, batchKey) {
  const read = parseBlocks(full, MISSIONS_BLOCKS, { batch: batchKey });
  if (read.acts.length) {
    _batches.set(batchKey, {
      refToData: new Map(), refToCard: new Map(), refToId: new Map(), inFlight: new Set(),
    });
  }
  return read;
}

// ── Lost access under an open tab ────────────────────────────────────────────
// A session can end while the tab is open — a deploy used to wipe every one of
// them, and a logout elsewhere or expiry still can — and a grant can be pulled.
// Nothing noticed: /auth/status runs once at boot, and fetchJson's 401 hook
// (showAuthGate) had been a no-op since React took the gate, so whichever page
// happened to fetch printed "Couldn't load now — not_authenticated".
//
// Same lifecycle as ink's checkEpHealth: re-check on tab return and every 5
// minutes (not on mount — boot's /auth/status just answered), and take any 401
// / ep_revoked from fetchJson (missions:auth-lost). Lost access crosses back to
// the landing, where <EpBanner> says why. A revoked grant needs nothing carried:
// /auth/status reports epRevoked on the way back. An ended session does — to
// /auth/status it is indistinguishable from never having signed in — so that
// one flag rides sessionStorage across the reload.
const SESSION_ENDED_KEY = 'missions:sessionEnded';
const LOST_ACCESS_AT_KEY = 'missions:lostAccessAt';
// Mirrors AVATAR_CLIENT_REFRESH_MS in identity.js (test/identity.test.js holds
// the two together): the avatar URL is signed for 60 minutes.
const AVATAR_REFRESH_MS = 30 * 60_000;

function useLostAccess() {
  useEffect(() => {
    let lost = false;
    const loseAccess = ({ reason } = {}) => {
      if (lost) return;
      lost = true;
      try {
        // A loop guard, not a feature: if the server and /auth/status ever
        // disagree about the grant, one reload is information and a second
        // inside 30s is a tab that cannot be used.
        if (Date.now() - Number(sessionStorage.getItem(LOST_ACCESS_AT_KEY) || 0) < 30_000) return;
        sessionStorage.setItem(LOST_ACCESS_AT_KEY, String(Date.now()));
        if (reason === 'session_expired') sessionStorage.setItem(SESSION_ENDED_KEY, '1');
      } catch { /* private mode: the landing just shows without the reason */ }
      // Hint first, so the reload lands on the landing's first frame.
      loginLineHint('missions').set(false);
      window.location.reload();
    };
    const checkEpHealth = async () => {
      if (lost) return;
      let res;
      try { res = await fetch('/api/ep-status', { credentials: 'same-origin' }); }
      catch { return; } // offline is not signed out
      if (res.status === 401) return loseAccess({ reason: 'session_expired' });
      if (res.status === 403) return loseAccess({ reason: 'revoked' });
      const data = await res.json().catch(() => null);
      if (data?.active === false) loseAccess({ reason: 'revoked' });
    };
    const onLost = (e) => loseAccess(e.detail || {});
    const onVis = () => { if (!document.hidden) checkEpHealth(); };
    document.addEventListener('missions:auth-lost', onLost);
    document.addEventListener('visibilitychange', onVis);
    const poll = setInterval(checkEpHealth, 5 * 60 * 1000);
    return () => {
      clearInterval(poll);
      document.removeEventListener('missions:auth-lost', onLost);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);
}

function AuthedApp({ userName, avatarUrl, userId }) {
  const [groups, setGroups] = useState([]);
  useLostAccess();
  const [activeGroupId, setActiveGroupId] = useState(() => {
    try { return localStorage.getItem('missions_new:activeGroupId'); } catch { return null; }
  });
  // The highlight only moves when a navTo* runs (wrapped on script load below),
  // and the legacy boot calls one ONLY for the three deep-link paths — landing
  // on the Missions index, its default, it calls none. So a hardcoded 'now'
  // here left the rail pointing at Now while Missions was on screen, for every
  // launch that didn't deep-link. Derive the same answer app.js's boot does.
  // Coupled to the routing at the bottom of legacy/app.js — change both together.
  const [activePage, setActivePage] = useState(() => {
    // The URL grammar is /<scope>/<page>[/<extra>], with the legacy unscoped
    // /<page> still honoured — so read the page out of whichever segment
    // names one, rather than matching whole paths.
    const seg = window.location.pathname.split('/').filter(Boolean);
    const PAGES = { home: 'home', now: 'now', lookback: 'lookback', manage: 'home', missions: 'missions', calendar: 'schedule', schedule: 'schedule' };
    for (const s of seg.slice(0, 2)) if (PAGES[s]) return PAGES[s];
    /* A path that names no page is HOME — the same answer parseRoute gives in
       legacy/app.js, which is the one that actually routes. Change both
       together. */
    return 'home';
  });
  // LEFT panel (the page menu) + BOTTOM panel (the console): shell-persisted
  // panel booleans — the rail's `panel` items and the panels themselves read
  // the same store by construction (usePanelOpen).
  // THE DRAWER OWNS THE FOLD (2026-08-22): the nav column folds by its own
  // <NavDrawer> grip/spine on the kit contract (html.ofself-nav-collapsed +
  // 'ofself:navCollapsed', restored pre-paint). The old missions-nav-open store and its mirror
  // effect retired — two sources of truth for one class fought each other.
  // The command palette's toggle flips the SAME contract directly.
  // a render-time read for the seats/hotkeys that still show the state;
  // not reactive, but those surfaces re-render on press anyway
  const navOpen = typeof document !== 'undefined'
    && !document.documentElement.classList.contains('ofself-nav-collapsed');
  // THROUGH THE GRIP (2026-09-20): the nav column's fold is the kit's
  // <NavDrawer> store (persisted under 'ofself:navCollapsed'), so flipping the
  // html class by hand would be overwritten on the next render. The palette
  // and the seats press the same grip a hand would — one fold, one owner.
  const toggleNav = useCallback(() => {
    document.querySelector('.ofself-nav-col .ofself-nav-drawer__grip')?.click();
  }, []);
  // THE HOME PAGE STANDS THE CONTENT PANEL DOWN. One class on <body> — the
  // same channel body.chat-closed uses — because the content panel holds
  // app.js's imperative DOM and must stay mounted through every page change;
  // hiding it in CSS is what lets the ledger take the surface without the
  // legacy engine ever being torn down.
  useEffect(() => {
    document.body.classList.toggle('missions-home', activePage === 'home');
  }, [activePage]);
  // THE MISSIONS COLUMNS' STATE — which legacy page stands (the engine's
  // missions:page), the census (missions:census), the standing view
  // (missions:view), and each column's fold (persisted like every drawer).
  const [missionPage, setMissionPage] = useState(null);
  const [census, setCensus] = useState({ missions: [], activeId: null, sort: 'tier' });
  // the open mission (missions:mission): null = loading, {error} = failure card
  const [missionData, setMissionData] = useState(null);
  // REACT OWNS THE VIEW now — the column's tiles set it; opening a mission
  // resets it to actions (the legacy resetMissionView law).
  const [missionView, setMissionViewState] = useState('outline');
  // Publish the view to the engine so the agent knows which one is in front of
  // the user. Signals is a VIEW of this page rather than a route, so routePage()
  // cannot see it and chatContext() has no other way to tell.
  useEffect(() => { window.setMissionView?.(missionView); }, [missionView]);
  useEffect(() => {
    const onPage = (e) => setMissionPage(e.detail);
    const onCensus = (e) => setCensus(e.detail);
    const onMission = (e) => {
      setMissionData(e.detail);
      // a NEW mission opens on actions; a refresh of the same one keeps the view
      setMissionViewState((v) => (e.detail && e.detail.id !== missionIdRef.current ? 'outline' : v));
      missionIdRef.current = e.detail?.id || null;
    };
    document.addEventListener('missions:page', onPage);
    document.addEventListener('missions:census', onCensus);
    document.addEventListener('missions:mission', onMission);
    return () => {
      document.removeEventListener('missions:page', onPage);
      document.removeEventListener('missions:census', onCensus);
      document.removeEventListener('missions:mission', onMission);
    };
  }, []);
  const missionIdRef = useRef(null);
  const inMissions = missionPage === 'page-missions' || missionPage === 'page-mission';
  const inMission = missionPage === 'page-mission';
  // THE COLUMNS FOLD BY HAND ONLY (owner call 2026-09-24, retiring the kit's
  // drawer ladder): every column — the nav, the census, the views — stands
  // OPEN by default and stays as you left it whatever you open; its grip folds
  // it, its grip or spine unfolds it, and the kit remembers each fold across
  // reloads (<TileColumn>). The census and views columns are ALWAYS MOUNTED
  // (the SplitViews below), so `standing` — the same test that opens their
  // panels — is only whether they are on the page, never a fold.

  // THE MISSIONS TILE'S SUB-LINE: the aggregate across all missions
  // (missions · goals · actions · open), announced by app.js whenever its
  // cache is fresh (missions:aggregate) and worn by the tile as quiet facts.
  const [missionsAgg, setMissionsAgg] = useState(null);
  useEffect(() => {
    const on = (e) => setMissionsAgg(e.detail && e.detail.missions ? e.detail : null);
    document.addEventListener('missions:aggregate', on);
    return () => document.removeEventListener('missions:aggregate', on);
  }, []);
  // Each figure on its own line (owner call 2026-08-23) — a small ledger, not
  // a sentence.
  const missionsSub = missionsAgg ? (
    <>
      <span>{missionsAgg.missions} missions</span>
      <span>{missionsAgg.goals} goals</span>
      <span>{missionsAgg.actions} actions</span>
      <span>{missionsAgg.open} open</span>
    </>
  ) : null;
  // RIGHT panel (the Agent): body.chat-closed is the source of truth — app.js
  // owns it (close / reopen / summon paths + the persisted chatClosed key), so
  // React MIRRORS the class instead of double-tracking its own boolean; the
  // observer keeps the rail glyph honest whichever seat flips the panel.
  // THE ECOSYSTEM DRAWER (the top lamp's wall, the fleet's law): the shell's
  // stage-head drawer — opening it squeezes the world below.
  const [ecoDrawerOpen, setEcoDrawerOpen] = useState(false);
  /* THE WALL'S ONE OBJECT, owned here (was inside <AgentPanel>). The wall and
     the frame's right upright are two seats onto the same reading, and the
     upright is a SIBLING OF THE STAGE — it cannot reach a fetch living inside
     the panel. One fetch up here feeds both. */
  const [identityPerms, setIdentityPerms] = useState(null);
  const [identityAgents, setIdentityAgents] = useState([]);
  useEffect(() => {
    let live = true;
    loadPermissions().then((x) => live && setIdentityPerms(x));
    loadAgents().then((x) => live && setIdentityAgents(x));
    return () => { live = false; };
  }, []);
  // FULL SCREEN is the kit's own mode (data-ofself-focus on <html>); this only
  // remembers it so the rail's head cell can light while it is on.
  const [focusMode, setFocus] = useState(() => isFocusMode());
  const setFocusMode = (on) => { setFocusModeKit(on); setFocus(!!on); };
  const [agentOpen, setAgentOpen] = useState(() => {
    try { return localStorage.getItem('missions_new:chatClosed') !== '1'; } catch { return true; }
  });
  // THE NUCLEUS (owner call 2026-08-17, parity across the fleet): the whole
  // ecosystem graph on the right edge, the kit's own surface draining this
  // app's /api/nucleus/* wire. ONE SURFACE PER EDGE — opening it puts the
  // agent away and the reverse, because two panels interleaved on one edge
  // read as broken glass rather than furniture (refractions' law).
  // the floating Quick Help card, summoned by the mission list's MENU
  const [nucleusOpen, setNucleusOpen] = useState(() => {
    try { return localStorage.getItem('missions:nucleusOpen') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('missions:nucleusOpen', nucleusOpen ? '1' : '0'); } catch (_) {}
  }, [nucleusOpen]);
  useEffect(() => {
    const sync = () => setAgentOpen(!document.body.classList.contains('chat-closed'));
    const mo = new MutationObserver(sync);
    mo.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => mo.disconnect();
  }, []);

  // Boot the legacy engine ONCE after panels exist in DOM.
  const setActivePageRef = useRef(setActivePage);
  setActivePageRef.current = setActivePage;
  useEffect(() => {
    if (window.__missionsBooted) return;
    window.__missionsBooted = true;
    const s = document.createElement('script');
    s.src = legacyAppUrl;
    // Intercept app.js's nav functions to sync the active page into React
    // state (the left panel's active row) without re-rendering the content
    // panel. The wrapping must wait for the script's LOAD event: app.js is a
    // classic script whose top-level `function navTo*` declarations land on
    // window only when it executes — wrappers installed before that get
    // clobbered (which is why the old rail's active highlight stuck on Now).
    s.onload = () => {
      /* The home tile is not a NAV section, but it navigates like one — so it
         is wrapped with them rather than left as the one page whose highlight
         could go stale (a deep link, a back button, the boot route). */
      [...NAV, { id: 'home', fn: 'navToHome' }].forEach(({ id, fn }) => {
        const original = window[fn];
        window[fn] = (...args) => {
          setActivePageRef.current(id);
          return original?.(...args);
        };
      });
      /* THE ENGINE IS UP — announced, because React surfaces that borrow
         app.js's functions (the home ledger counts its queue with the Now
         page's own bucketForItem) can render BEFORE this script has loaded:
         the boot route lands on home while the <script> is still in flight.
         Without a signal they compute once against a missing global and sit
         on zeros forever. */
      window.__missionsEngineReady = true;
      document.dispatchEvent(new Event('missions:engine-ready'));
    };
    document.body.appendChild(s);
  }, []);

  // The group list, or null when it could not be read.
  const loadGroups = useCallback(() => fetch('/api/groups', { credentials: 'same-origin' })
    .then((r) => (r.ok ? r.json() : null))
    .then((d) => (d ? (d.groups || []).map(toShellGroup) : null)), []);

  // Each group's photo is a URL signed for an hour: re-read the list on the
  // avatar beat. A failed read keeps the list we have.
  useEffect(() => {
    const id = setInterval(() => {
      loadGroups()
        .then((list) => { if (list) setGroups(list); })
        .catch(() => { /* the next tick asks again */ });
    }, AVATAR_REFRESH_MS);
    return () => clearInterval(id);
  }, [loadGroups]);

  useEffect(() => {
    loadGroups()
      .then((loaded) => {
        const list = loaded || [];
        setGroups(list);
        // THE URL'S SCOPE WINS — the same reconciliation applyBootScope does in
        // legacy/app.js, for the same reason and against the same group list.
        // Coupled to it; change both together.
        //
        // activeGroupId above seeds from localStorage and NOTHING else ever
        // revisits it, so this is not a race like the engine's was — left
        // alone, a pasted group link makes this console name last session's
        // space for the whole session, over pages showing the group.
        const seg = window.location.pathname.split('/').filter(Boolean);
        const slug = seg.length && !ROUTE_PAGE_SEGMENTS.includes(seg[0])
          ? decodeURIComponent(seg[0]) : null;
        if (!slug) return;
        const hit = list.find((g) => slugifyScope(g.name) === slug);
        if (hit) setActiveGroupId(hit.id);
        else if (slug === slugifyScope(userName && userName !== 'Connected' ? userName : 'me')) {
          setActiveGroupId(null);
        }
      })
      .catch(() => {});
  }, [userName, loadGroups]);

  const activeGroup = groups.find((g) => g.id === activeGroupId) || null;
  const groupsAuth = Object.fromEntries(groups.map((g) => [g.id, g.app_authorized !== false]));

  // THE COMMAND DOCK's list — stable identity (the shell re-registers its
  // hotkey listener when `commands` changes). toggleNav is store-bound;
  // the agent seams are module-scope.
  const dockCommands = useMemo(
    () => buildMissionsCommands({ toggleSidebar: toggleNav, toggleAgent, openAgent }),
    [toggleNav],
  );


  return (
    <>
    {/* THE GRANT IS LIVE BUT NO LONGER COVERS WHAT MISSIONS DECLARED — a realm
        tightened below the DLR, or the DLR grew. Paradigm computes it and
        /my-permissions returns it; until now the only trace was an alarm dot on
        the Exposure tab of a wall nobody had open, so the app simply failed on
        the parts it had lost and looked fine everywhere else. It rides ABOVE
        the ShellProvider so it is the first thing on the page rather than
        furniture inside a frame the condition outranks. */}
    {/* THE ACTIVE GROUP is the third boundary. A group grants its own access,
        so a person fully authorized for Missions still gets 403
        GROUP_APP_NOT_AUTHORIZED in a group that never connected it (or revoked
        or paused it). That used to surface only as raw API text in the missions
        list, and as a Connect chip in a switcher someone arriving by URL never
        opens. `activeGroup` is null until /api/groups answers, so the banner
        cannot flash before the flag is known. */}
    <EpBanner reauth={identityPerms?.reauth || null} group={activeGroup} />
    <ShellProvider
      value={{
        currentUser: { username: userName || 'You', avatarUrl },
        groups,
        activeGroup,
        groupsAuth,
        onSwitchSpace: (space) => {
          if (space) { window.switchToGroup?.(space.id, space.name); setActiveGroupId(space.id); }
          else { window.switchToPersonal?.(); setActiveGroupId(null); }
        },
        // Groups that haven't authorized missions render with a Connect chip.
        // Clicking it starts the group authorize round-trip: /auth/connect-group
        // redirects to Paradigm's /authorize?group_id=… consent page, which
        // bounces back through /callback; the reload refetches /api/groups and
        // the group's app_authorized flips, turning the chip into a normal
        // switchable space (ink's pattern, server.js's route).
        onConnectSpace: (space) => {
          if (!space?.id) return;
          window.location.href = `/auth/connect-group?group_id=${encodeURIComponent(space.id)}`;
        },
        onSignOut: () => {
          // Drop the boot hint FIRST — the post-signout reload must land on
          // the landing's first frame, not flash the app shell.
          loginLineHint('missions').set(false);
          window.disconnect?.();
        },
        // Log out of the whole ecosystem: relays to Paradigm's ecosystem-logout,
        // which ends every app's session and fans session.revoked to the others.
        onSignOutEcosystem: () => {
          loginLineHint('missions').set(false);
          window.disconnectEcosystem?.();
        },
        fetchAuthorizedApps: () => {
          const url = activeGroup?.id
            ? `/api/app-shell?group_id=${encodeURIComponent(activeGroup.id)}`
            : '/api/app-shell';
          return fetch(url, { credentials: 'same-origin' })
            .then((r) => (r.ok ? r.json() : { apps: [] }))
            .catch(() => ({ apps: [] }));
        },
      }}
    >
      <Shell
        currentApp="missions"
        /* SETTINGS › MANAGE (owner call 2026-09-20): the Manage page moved
           into the kit's Settings desk as a host pane. The desk mounts a
           pane's content only while it stands, so Manage loads its catalog
           on arrival — `active` is always true here. */
        settings={MISSIONS_SETTINGS_PANES}
        /* THE DOCK (shell 0.257.0 — the rail is gone): the avatar floating
           bottom-left raises the ecosystem switcher — spaces, the wall's
           readings, Preferences, Apps, the way out. Full screen is the kit's
           FOCUS seat in the top-right array. Search, Personas, Preferences
           and Help are the mission list's own MENU (missionListItems). */
        dock
        focus={focusMode}
        onFocusChange={setFocusMode}
        /* THE APP RAIL (owner call 2026-09-22): the kit's one set down the
           far left — the missions mark (home, lit while you are here),
           Search, Agent, Preferences, Help, and the avatar in the last equal
           block. The app switches it on; the kit seats it. Search here is
           the command dock. */
        rail
        drawer={<EcosystemTable currentApp="missions" />}
        drawerOpen={ecoDrawerOpen}
        /* THE COMMAND DOCK — the bottom panel (⌘J): commands + transcript.
           A line the grammar can't parse and the list can't match is natural
           language — route it to the agent panel's thread (the fleet grammar). */
        commands={dockCommands}
        onCommandFallthrough={askAgent}
        onSearch={toggleDock}
      >
          <TileColumn
            data-qh-title="Workspace"
            data-qh-body="Your domains of care — the page menu, the current page, and the Agent, arranged as panels."
            /* THE PAGE COLUMN — the app's nav column. ALWAYS OPEN to
               the SplitView (2026-08-22): the drawer owns the fold —
               html.ofself-nav-collapsed narrows it to the spine, whose SITE
               MAP is the same items the tiles render. Its seam writes
               --ofself-nav-col-w on the column itself. */
            nav
            storageKey="missions-nav-width"
            label="Pages"
            /* THE FIRST TILE IS THE APP ITSELF — the shell draws missions'
               register cell from <Shell currentApp> and pressing it opens the
               home ledger. One word here; no mark, name or number typed
               app-side. The page is a legacy route like any other
               (navToHome), so the URL, back / forward and a refresh behave. */
            home="home"
            items={missionsSub
              ? NAV.map((n) => (n.id === 'missions' ? { ...n, sub: missionsSub } : n))
              : NAV}
            active={activePage}
            onSelect={goToPage}
            columnClassName="missions-nav-panel"
            columnProps={{
              id: 'missions-page-column',
              'data-qh-title': 'Page menu',
              'data-qh-body': 'Missions’ pages — Missions, Now, Calendar, Lookback. The rail’s left toggle folds it away.',
            }}
            trailing={<>{activePage === 'home' && (
              /* THE FRONT DOOR. It stands BESIDE the content panel rather
                 than replacing it: ContentPanel hosts app.js's imperative DOM
                 and is memoized with no props so it mounts exactly once —
                 unmounting it to swap pages would wipe the legacy engine's
                 whole world. So the ledger mounts and the content panel is
                 hidden by the body class below (body.chat-closed's idiom: one
                 channel, spoken in CSS). */
              <main className="block block--fill missions-home-panel"
                    data-qh-title="Home"
                    data-qh-body="Missions' front door — what is waiting today, what you are trying to do, and what you closed this week.">
                {/* the fade rides the CONTENT, never the .block: a fading
                    panel un-paints its own surface and the field (lattice +
                    app mark) leaks through for the whole rise */}
                <MissionsOverview className="ofself-page-fade" />
              </main>
            )}
              {/* THE MISSIONS COLUMNS — nested <TileColumn>s, ALWAYS MOUNTED
                  so ContentPanel (the legacy engine's one home) never
                  remounts: leaving the section just slides the columns shut
                  (`standing`), the kit's own transition. Width, seam, fold,
                  spine and tiles are all the kit's; the census and views are
                  just two more instances of the page column. */}
              <TileColumn
                className="missions-mid"
                /* THE CENSUS: on the page while the missions section stands,
                   open unless its grip folded it. ONE
                   SOURCE OF TRUTH, ONE HANDLE EACH (owner call 2026-09-10): it
                   rests on --ofself-nav-col-w like its neighbours, and its
                   seam gives it a width of its own once dragged. */
                standing={inMissions}
                storageKey="missions-side-width"
                label="Missions"
                /* tier and abandonment ride as STATE (data-tier,
                   is-abandoned) — missions.css dims them; the tiles are the
                   kit's own recipe */
                items={census.missions.map((m) => ({
                  id: m.id,
                  label: m.name,
                  count: m.open,
                  data: { tier: m.tier },
                  className: m.abandoned ? 'is-abandoned' : undefined,
                }))}
                active={census.activeId}
                onSelect={(id) => { if (id !== census.activeId) window.openMission?.(id); }}
                navClassName="missions-census-nav"
                /* THE HEAD STAYS WHEN THE LIST FOLDS (kit 0.232.0): the same
                   icons, stacked under the grip */
                head={<PanelHeader spread items={missionListItems(census)} />}
                spineTools={<PanelMenu items={missionListItems(census)} side="right" />}
                columnClassName="missions-side-panel"
                columnProps={{
                  'data-qh-title': 'Missions',
                  'data-qh-body': 'Every mission — press one to open it; the grip folds the column to its spine.',
                }}
                trailing={
                  <TileColumn
                    /* THE VIEWS — the mission's readings, standing while a
                       mission is open (the same law, 2026-09-10) */
                    standing={inMission}
                    storageKey="missions-views-width"
                    label="Mission views"
                    items={MISSION_VIEWS.map((v) => ({ id: v.key, label: v.label }))}
                    active={missionView}
                    onSelect={setMissionViewState}
                    navClassName="missions-viewcol-nav"
                    columnClassName="missions-viewcol-panel"
                    columnProps={{
                      'data-qh-title': 'Views',
                      'data-qh-body': 'The mission’s readings — goals, actions, board, signals.',
                    }}
                    trailing={
                      <>
                        <ContentPanel />
                        {/* THE PORTED PAGES — React content inside the legacy
                            page containers (see LegacyPagePortal). `active`
                            gates their fetches to when the page stands. */}
                        <LegacyPagePortal target="page-builder">
                          <Builder active={missionPage === 'page-builder'} mission={missionData} />
                        </LegacyPagePortal>
                        <LegacyPagePortal target="page-mission">
                          <Mission active={missionPage === 'page-mission'}
                                   mission={missionData} view={missionView} />
                        </LegacyPagePortal>
                        <LegacyPagePortal target="page-now">
                          <Now active={missionPage === 'page-now'} userId={userId} />
                        </LegacyPagePortal>
                        <LegacyPagePortal target="page-schedule">
                          <Schedule active={missionPage === 'page-schedule'} />
                        </LegacyPagePortal>
                        <LegacyPagePortal target="page-lookback">
                          <Lookback active={missionPage === 'page-lookback'} />
                        </LegacyPagePortal>
                      </>
                    }
                  />
                }
              />
              {/* THE DETAIL PANEL — the kit's right <SlideOutPanel>, seated
                  just inside the agent wall (src/pages/Sheet.jsx) */}
              <Sheet agentOpen={agentOpen} closeAgent={closeAgent} />
              <AgentPanel open={agentOpen} onClose={closeAgent} onOpen={openAgent}
                          identityName={userName} groupId={activeGroupId}
                          perms={identityPerms} agents={identityAgents} />
              {/* the hidden legacy scaffolding rides AFTER the panel: as a
                  sibling BETWEEN the content and the panel it broke the
                  `.block + .block` adjacency the kit hands the dividing
                  hairline to, and the agent panel lost its left seam — an
                  invisible div deleting a visible line. */}
              <AgentBody />
              {/* the nucleus slideout retired with the tesseract (2026-08-25):
                  the wall's Nucleus tab is the one door to the graph now */}</>}
          />
      </Shell>
    </ShellProvider>
    </>
  );
}

// ── Dev login ─────────────────────────────────────────────────────────────────
const DEV = typeof window !== 'undefined'
  && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

function DevLoginTray() {
  if (!DEV) return null;
  return (
    <>
      <span className="landing__dev-label">Dev quick-login</span>
      <div className="landing__dev-row">
        <button className="landing__dev-btn"
                onClick={() => { window.location.href = '/auth/dev-login'; }}>
          Continue as dev
        </button>
      </div>
    </>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────
// NO-FLICKER BOOT: the shell's useLoginLine boots the first frame into the
// last known side of the login line (persisted as missions:wasAuthed) and
// corrects from /auth/status afterward — a refresh never flashes the rail'd
// app shell over the landing or vice versa.
// Written by the engine (legacy/app.js rememberUserName) whenever /auth/profile
// answers. Keyed by user so a shared browser never greets one person by another's
// name.
function lastKnownUserName(userId) {
  if (!userId) return null;
  try { return localStorage.getItem(`missions:userName:${userId}`); } catch { return null; }
}

export default function App() {
  // /auth/status's last answer, for the landing. useLoginLine hands `user`
  // through on the authed side only — it resets it to null on 'anon' — so the
  // landing's `auth.user?.epRevoked` was always undefined and the revoked
  // banner could never render.
  const statusRef = useRef(null);
  const auth = useLoginLine({
    app: 'missions',
    check: async () => {
      const s = await fetch('/auth/status', { credentials: 'same-origin' }).then((r) => r.json());
      statusRef.current = s;
      return { authed: !!s.authenticated && !s.epRevoked, user: s };
    },
  });
  // Set by useLostAccess just before it reloaded. Read once and dropped, so the
  // reason shows on the landing it caused and not on every refresh after.
  const [sessionEnded] = useState(() => {
    try { return sessionStorage.getItem(SESSION_ENDED_KEY) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { sessionStorage.removeItem(SESSION_ENDED_KEY); } catch { /* private mode */ }
  }, []);
  // THE NAME ARRIVES AFTER THE LOGIN LINE. /auth/status answers with the
  // authorization alone when the display name isn't cached server-side (its
  // profile read is seconds on a cold server); the engine fetches /auth/profile
  // alongside and announces the answer. Until then: the last name this browser
  // saw for this user.
  const [liveName, setLiveName] = useState(null);
  useEffect(() => {
    const onName = (e) => setLiveName(e.detail || null);
    document.addEventListener('missions:user-name', onName);
    return () => document.removeEventListener('missions:user-name', onName);
  }, []);

  // THE AVATAR ARRIVES THE SAME WAY, and it EXPIRES: it is a signed URL, good for
  // 60 minutes. `undefined` = not heard yet (use /auth/status's copy); `null` = no
  // photo. While signed in, ask again on AVATAR_REFRESH_MS so an open tab never
  // holds a dead one — identity.js pins how that fits inside the signature.
  const [liveAvatar, setLiveAvatar] = useState(undefined);
  useEffect(() => {
    const onAvatar = (e) => setLiveAvatar(e.detail || null);
    document.addEventListener('missions:user-avatar', onAvatar);
    return () => document.removeEventListener('missions:user-avatar', onAvatar);
  }, []);
  const signedIn = auth.state !== 'checking' && auth.state !== 'anon';
  useEffect(() => {
    if (!signedIn) return undefined;
    const id = setInterval(() => {
      fetch('/auth/profile', { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => { if (d) setLiveAvatar(d.avatarUrl || null); })
        .catch(() => { /* keep what we have; the next tick asks again */ });
    }, AVATAR_REFRESH_MS);
    return () => clearInterval(id);
  }, [signedIn]);

  if (auth.state === 'checking') {
    // First visit only (no hint yet): a neutral blank field — no rail, no
    // landing chrome — so neither side of the login line leaks.
    return <Shell currentApp="missions" bareStage><div /></Shell>;
  }

  if (auth.state === 'anon') {
    return (
      <>
        {/* A REVOKED SESSION IS NOT AN ANONYMOUS ONE. `authed` goes false for
            both, so both used to land here and read as "please sign in" — the
            person was never told their access had been withdrawn, and the
            reauthUrl /auth/status handed us was dropped on the floor. Nor is an
            ENDED one: a session that died under an open tab lands here too. */}
        <EpBanner
          expired={sessionEnded}
          revoked={Boolean(statusRef.current?.epRevoked)}
          reauthUrl={statusRef.current?.reauthUrl || null}
        />
        {/* Top-left hamburger → full-screen overlay, provided by the shell — the
            same LandingMenu + LandingNav synapse, narratives, and the design
            wireframe mount. Only the MISSIONS·OFSELF wordmark for now. */}
        <LandingMenu activeId="home" variant="home" foot={<ThemeToggle />}>
          {(close) => (
            <LandingNav brand="MISSIONS" ofself brandHref="/" activeId="home" onSelect={close} />
          )}
        </LandingMenu>
        <Landing
          currentApp="missions"
          authorizeUrl="/auth/connect"
          wordmark="MISSIONS"
          bottomRight="Life intentionality — your domains of care, in one place."
          dev={<DevLoginTray />}
        />
      </>
    );
  }

  const userId = auth.user?.userId || null;
  const userName = liveName || auth.user?.userName || lastKnownUserName(userId);
  const avatarUrl = liveAvatar !== undefined ? liveAvatar : (auth.user?.avatarUrl || null);
  return <AuthedApp userName={userName} avatarUrl={avatarUrl} userId={userId} />;
}
